import { Inject, Injectable } from '@angular/core';

import { Platform } from '@ionic/angular/standalone';

import { App } from '@capacitor/app';

import { DateTime } from 'luxon';
import mixpanel, { Mixpanel } from 'mixpanel-browser';

import { EnvironmentInterface } from '../../types';

@Injectable({
    providedIn: 'root'
})
export class MixpanelService {
    enableMixpanel: boolean = true;
    isInit: boolean = false;
    /* eslint-disable @typescript-eslint/no-explicit-any */
    private environment: EnvironmentInterface;
    private propertyNames = {
        marketing_agreement: 'Marketing agreement',
        user_status: 'User status',
        referral: 'Referral source',
        last_booking: 'Last booking request'
    };

    private bookingStatuses = {
        requested: 'Requested',
        accepted: 'Accepted',
        completed: 'Completed',
        cancelled: 'Cancelled by Customer',
        cancelled_by_pro: 'Cancelled by Pro',
        rejected: 'Rejected by Pro'
    };

    /**
     * constructor
     * get mixpanel token and initialize
     */
    constructor(
        private platform: Platform,
        @Inject('environment') environment: EnvironmentInterface
    ) {
        this.environment = environment;
        this.enableMixpanel = environment?.analytics?.enableMixpanelAnalytics || false;
    }

    getUTCDatetime(datetime: string) {
        const localDatetime = DateTime.fromISO(`${datetime.replace(' ', 'T')}`);
        const utcISOString = localDatetime.toUTC();
        const utcFormat = utcISOString.toString().split('.')[0];
        return utcFormat;
    }

    /**
     * Initialize mixpanel.
     */
    init(): void {
        if (!this.environment || !this.environment.analytics.mixpanelToken) {
            console.error('Missing Mixpanel Token in environment file');
            this.isInit = false;
            return;
        } else if (!this.enableMixpanel) {
            console.info('Mixpanel has been disabled');
            this.isInit = false;
            return;
        }
        mixpanel.init(this.environment.analytics.mixpanelToken);
        this.setup();
    }

    disableMixpanel() {
        mixpanel.disable();
        this.isInit = false;
    }

    /**
     * Create new Alias for user
     * Call this method only once in user lifecycle
     *
     * @param {string} alias
     */
    createAlias(alias: string) {
        if (!this.isInit) return;
        mixpanel.alias(alias, mixpanel.get_distinct_id());
    }

    /**
     * Identify User
     *
     * @param {string} alias
     */
    identify(alias: string) {
        if (!this.isInit) return;
        mixpanel.identify(alias);
    }

    /**
     * Push new action to mixpanel.
     *
     * @param {string} id Name of the action to track.
     * @param {*} [action={}] Actions object with custom properties.
     * @memberof MixpanelService
     */
    track(id: string, action = {}): void {
        if (!this.isInit) return;
        mixpanel.track(id, action);
    }

    /**
     * setup mixpannel
     *
     */

    async setup() {
        const appInfo = await App.getInfo();

        const platforms = this.platform.platforms();
        let p = 'Web App';

        if (platforms.indexOf('cordova') > -1 && platforms.indexOf('mobile') > -1) {
            p = 'Mobile App';
        }

        this.setSuperProperties({
            Platform: p,
            'App Version': appInfo.version
        });

        this.isInit = true;
    }

    /**
     * setPeople
     * Store user profiles in Mixpanel's People Analytics product
     * @param {Object} properties
     */
    setPeople(properties = {}): void {
        if (!this.isInit) return;
        mixpanel.people.set(properties);
    }

    /**
     * setSuperProperties
     *
     * @param {object} properties
     */
    setSuperProperties(properties: object) {
        if (!this.isInit) return;
        mixpanel.register(properties);
    }

    /**
     * sendEvent
     *
     * @param {string} event
     * @param {object} properties
     */
    sendEvent(event: string, properties: Mixpanel | { [key: string]: any } | undefined) {
        if (!this.isInit) return;
        if (properties) {
            mixpanel.track(event, properties);
        } else {
            this.trackEvent(event);
        }
    }

    /**
     * trackEvent
     * @param {String} event
     */
    trackEvent(event: string) {
        if (!this.isInit) return;
        mixpanel.track(event);
    }

    /**
     * Reset Mixpanel
     */
    logout() {
        if (!this.isInit) return;
        mixpanel.reset();
    }

    trackCharge(amount: number, datetime: string | null = null, properties = {}) {
        if (!this.isInit) return;
        const timeObj = {
            $time: datetime ? datetime.replace(' ', 'T') : undefined
        };

        const obj = {
            ...timeObj,
            ...properties
        };

        mixpanel.people.track_charge(amount, obj);
    }

    increment(amount: number, event: string) {
        if (!this.isInit) return;
        mixpanel.people.increment(event, amount);
    }

    userRegistered(user: any, event = 'Visit') {
        if (!this.isInit) return;
        const createdDate = this.getUTCDatetime(user['created_at']);

        this.createAlias(user['id']);

        this.setPeople({
            $avatar: user['photo_url'],
            $distinct_id: user['id'],
            $first_name: user['first_name'],
            $last_name: user['last_name'],
            $email: user['email'],
            $phone: user['phone'],
            $created: createdDate,
            Id: user['id'],
            [this.propertyNames.marketing_agreement]: user['agreed_marketing'] === 1 ? true : false,
            [this.propertyNames.user_status]: 'Signed-up user',
            [this.propertyNames.referral]: user['referral_source'],
            'Lifetime booking request': 0,
            'Total revenue': 0
        });

        this.identify(user['id']);
        this.sendEvent(event, undefined);

        this.setSuperProperties({
            [this.propertyNames.marketing_agreement]: user['agreed_marketing'] === 1 ? true : false,
            [this.propertyNames.user_status]: 'Signed-up user',
            'Account creation date': createdDate
        });
    }

    userLoggedIn(user: any) {
        if (!this.isInit) return;

        //datetime needs to be utc for mixpanel
        const createdDate = this.getUTCDatetime(user['created_at']);

        this.setPeople({
            $avatar: user['photo_url'],
            $distinct_id: user['id'],
            $first_name: user['first_name'],
            $last_name: user['last_name'],
            $email: user['email'],
            $phone: user['phone'],
            $created: createdDate,
            Id: user['id'],
            [this.propertyNames.marketing_agreement]: user['agreed_marketing'] === 1 ? true : false,
            [this.propertyNames.user_status]: 'Signed-up user',
            [this.propertyNames.referral]: user['referral_source']
        });

        mixpanel.people.set_once({
            'Lifetime booking request': 0,
            'Total revenue': 0
        });

        this.identify(user['id']);
        this.sendEvent('User logged in', undefined);

        this.setSuperProperties({
            [this.propertyNames.marketing_agreement]: user['agreed_marketing'] === 1 ? true : false,
            [this.propertyNames.user_status]: 'Signed-up user',
            'Account creation date': createdDate
        });
    }

    updateMarketingStatus(status: string) {
        if (!this.isInit) return;

        this.setSuperProperties({
            [this.propertyNames.marketing_agreement]: status
        });
    }

    updateUserStatus(status: string) {
        if (!this.isInit) return;

        this.setSuperProperties({
            [this.propertyNames.user_status]: status
        });
    }

    sendBookingInfo(data: any, status: string, paid: boolean = false) {
        if (!this.isInit) return;

        let cancelledPaid = '';
        if (status === 'cancelled') {
            if (paid === true) {
                cancelledPaid = 'Late ';
            } else {
                cancelledPaid = 'Advanced ';
            }
        }

        this.sendEvent('Make a booking request', {
            'Provider name': data['pro_name'],
            'Booking value': data['amount'],
            'Booking status': [`${cancelledPaid}${status}`],
            'Service booked': [...data['services']],
            'Major category': [...data['categories']]
        });
    }

    sentBookingRequest(data: any) {
        if (!this.isInit) return;

        this.sendBookingInfo(data, 'requested');

        const createdAtDate = this.getUTCDatetime(data['created_at']);

        this.setPeople({
            'Last booking request': createdAtDate
        });

        mixpanel.register_once({
            'First booking request': createdAtDate
        });

        this.increment(Number(data['amount']), 'Total revenue');

        this.increment(1, 'Lifetime booking request');
    }

    pageViewed(page: string, additionInfoObject: Mixpanel | { [key: string]: any } | undefined = undefined) {
        if (!this.isInit) return;

        this.sendEvent(page, additionInfoObject);
    }

    errorEvent(type: string, object: any) {
        if (!this.isInit) return;

        const errorType = type === 'user' ? 'User Error' : 'System Error';
        const mpObj = {
            ...object,
            'Error type': [errorType]
        };

        this.sendEvent('Error', mpObj);
    }
}
